// BatchLoggerService for the AlphaMind IDO Frontend logging system
import { LogEvent, LogBatchRequest, LogBatchResponse, LoggingConfig, LoggingError } from '../types/logging.types';

export class BatchLoggerService {
  private batch: LogEvent[] = [];
  private flushTimer: number | null = null;
  private periodicTimer: number | null = null;
  private isOnline: boolean = navigator.onLine;
  private retryQueue: LogEvent[] = [];
  private isRetrying: boolean = false;
  private isFlushing: boolean = false;
  private isUnloading: boolean = false;
  private sentLogIds: Set<string> = new Set(); // Track sent logs to prevent duplicates
  private readonly STORAGE_KEY = 'alphamind_failed_logs';
  private readonly MAX_STORAGE_SIZE = 1000; // Maximum number of logs to store locally

  constructor(private config: LoggingConfig) {
    this.setupEventListeners();
    this.loadFailedLogsFromStorage();
    this.startPeriodicFlush();
  }

  // Add log to batch
  public addLog(log: Partial<LogEvent>): void {
    if (!this.config.enabled) return;

    // Generate unique ID for log if not present
    const logWithId = {
      ...log,
      id: log.id || this.generateLogId(log)
    } as LogEvent;

    // Check if log already sent to prevent duplicates
    if (this.sentLogIds.has(logWithId.id)) {
      if (this.config.consoleEnabled) {
        console.warn('Duplicate log prevented:', logWithId.id);
      }
      return;
    }

    this.batch.push(logWithId);

    // Flush if batch size reached
    if (this.batch.length >= this.config.batchSize) {
      this.flush();
    }

    // Start flush timer if not already running
    if (!this.flushTimer) {
      this.startFlushTimer();
    }
  }

  // Manually flush current batch
  public async flush(): Promise<void> {
    if (this.batch.length === 0 || this.isFlushing) return;

    this.isFlushing = true;

    const logsToSend = [...this.batch];
    this.batch = [];

    // Clear flush timer
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    try {
      // Use Promise.race with timeout to prevent hanging requests that block page loading
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
      });

      await Promise.race([this.sendLogs(logsToSend), timeoutPromise]);

      // Mark logs as sent to prevent duplicates
      logsToSend.forEach(log => {
        this.sentLogIds.add(log.id);
      });

      // Clean up old sent log IDs to prevent memory leak
      this.cleanupSentLogIds();

    } catch (error) {
      // Check if this is a connection refused error - if so, don't retry to prevent page blocking
      if (error instanceof Error &&
          (error.message.includes('ERR_CONNECTION_REFUSED') ||
           error.message.includes('Failed to fetch'))) {
        console.warn('Logging backend unavailable - discarding logs to prevent page blocking');
        // Don't add to retry queue for connection refused errors
      } else {
        // For other errors, try to add to retry queue but don't block if it fails
        try {
          this.addToRetryQueue(logsToSend);
        } catch (retryError) {
          console.warn('Failed to add logs to retry queue:', retryError);
        }
      }
      console.warn('Log flush failed silently (page loading continues):', error);
    } finally {
      this.isFlushing = false;
    }
  }

  // Get current batch (for debugging)
  public getCurrentBatch(): LogEvent[] {
    return [...this.batch];
  }

  // Get retry queue size (for debugging)
  public getRetryQueueSize(): number {
    return this.retryQueue.length;
  }

  // Force immediate flush (for critical events like wallet changes)
  public async forceFlush(): Promise<void> {
    if (this.batch.length === 0) return;

    // Clear any existing timers to prevent conflicts
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    // Use non-blocking flush to prevent page loading issues
    this.flush().catch(error => {
      console.warn('Force flush failed, but continuing silently:', error);
    });
  }

  // Check if there are pending logs that need to be sent
  public hasPendingLogs(): boolean {
    return this.batch.length > 0 || this.retryQueue.length > 0;
  }

  // Send logs to backend with enhanced error handling to prevent page loading issues
  private async sendLogs(logs: LogEvent[]): Promise<void> {
    if (!this.isOnline) {
      throw new LoggingError('Device is offline');
    }

    const request: LogBatchRequest = { logs };

    try {
      // Create AbortController for request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // Reduced to 3 second timeout

      const response = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Don't throw for any HTTP errors to prevent blocking page loading
        console.warn(`HTTP ${response.status} when sending logs, continuing silently`);
        return;
      }

      const result: LogBatchResponse = await response.json().catch(() => ({ success: true }));

      if (!result.success) {
        console.warn('Server reported log failure, continuing silently');
        return;
      }

      // Log success in development
      if (this.config.consoleEnabled) {
        console.log(`Successfully sent ${logs.length} logs to backend`);
      }

    } catch (error) {
      // Handle connection refused and other network errors silently
      if (error instanceof Error) {
        if (error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('Failed to fetch') ||
            error.name === 'AbortError') {
          console.warn('Logging backend unavailable, disabling logging temporarily');
          // Don't throw - just return silently to prevent page loading issues
          return;
        }
      }

      // For any other errors, also return silently to prevent page blocking
      console.warn('Logging error (continuing silently):', error);
      return;
    }
  }

  // Send logs using sendBeacon (for page unload)
  private sendLogsWithBeacon(logs: LogEvent[]): boolean {
    if (!navigator.sendBeacon) return false;

    const request: LogBatchRequest = { logs };
    const blob = new Blob([JSON.stringify(request)], { type: 'application/json' });

    return navigator.sendBeacon(this.config.endpoint, blob);
  }

  // Add logs to retry queue with connection check
  private addToRetryQueue(logs: LogEvent[]): void {
    // Don't add to retry queue if we have too many failed logs (backend likely down)
    if (this.retryQueue.length > 100) {
      console.warn('Too many failed logs, discarding to prevent memory issues');
      return;
    }

    this.retryQueue.push(...logs);
    this.saveFailedLogsToStorage();

    // Start retry process if not already running
    if (!this.isRetrying) {
      this.startRetryProcess();
    }
  }

  // Start retry process with exponential backoff
  private startRetryProcess(): void {
    if (this.isRetrying || this.retryQueue.length === 0) return;

    this.isRetrying = true;
    let retryAttempt = 0;

    const retry = async () => {
      if (this.retryQueue.length === 0) {
        this.isRetrying = false;
        return;
      }

      if (!this.isOnline) {
        // Wait for online status
        setTimeout(retry, 5000);
        return;
      }

      const logsToRetry = this.retryQueue.splice(0, this.config.batchSize);

      // Filter out already sent logs to prevent duplicates
      const unsent = logsToRetry.filter(log => !this.sentLogIds.has(log.id));

      if (unsent.length === 0) {
        // All logs already sent, continue with next batch
        setTimeout(retry, 1000);
        return;
      }

      retryAttempt++;

      try {
        await this.sendLogs(unsent);

        // Mark logs as sent
        unsent.forEach(log => {
          this.sentLogIds.add(log.id);
        });

        // Success - continue with next batch
        retryAttempt = 0; // Reset retry attempt counter
        setTimeout(retry, 1000); // Short delay before next batch

      } catch (error) {
        // Failed - add back to queue and wait with exponential backoff
        this.retryQueue.unshift(...unsent);

        if (retryAttempt >= this.config.retryAttempts) {
          // Max retries reached - save to storage and stop retrying this batch
          this.saveFailedLogsToStorage();
          this.isRetrying = false;

          if (this.config.consoleEnabled) {
            console.error(`Max retry attempts reached for ${unsent.length} logs`);
          }
          return;
        }

        // Exponential backoff: 2^attempt * base delay
        const delay = Math.min(this.config.retryDelay * Math.pow(2, retryAttempt - 1), 60000);
        setTimeout(retry, delay);
      }
    };

    // Start retry process
    setTimeout(retry, this.config.retryDelay);
  }

  // Save failed logs to localStorage
  private saveFailedLogsToStorage(): void {
    try {
      const existingLogs = this.loadFailedLogsFromStorage();
      const allFailedLogs = [...existingLogs, ...this.retryQueue];

      // Limit storage size
      const logsToStore = allFailedLogs.slice(-this.MAX_STORAGE_SIZE);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logsToStore));
    } catch (error) {
      console.error('Failed to save logs to localStorage:', error);
    }
  }

  // Load failed logs from localStorage
  private loadFailedLogsFromStorage(): LogEvent[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const logs = JSON.parse(stored) as LogEvent[];
        this.retryQueue.push(...logs);
        localStorage.removeItem(this.STORAGE_KEY); // Clear after loading
        return logs;
      }
    } catch (error) {
      console.error('Failed to load logs from localStorage:', error);
    }
    return [];
  }

  // Setup event listeners for lifecycle management
  private setupEventListeners(): void {
    // Online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      if (this.retryQueue.length > 0 && !this.isRetrying) {
        this.startRetryProcess();
      }
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    // Page unload - try to send remaining logs
    window.addEventListener('beforeunload', () => {
      this.handlePageUnload();
    });

    // Page visibility change - flush when page becomes hidden
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.handlePageUnload();
      }
    });

    // Page freeze (mobile browsers)
    window.addEventListener('freeze', () => {
      this.handlePageUnload();
    });
  }

  // Handle page unload
  private handlePageUnload(): void {
    if (this.isUnloading) return; // Prevent multiple unload handlers
    this.isUnloading = true;

    // Try to flush current batch
    if (this.batch.length > 0) {
      // Filter out already sent logs
      const unsentBatch = this.batch.filter(log => !this.sentLogIds.has(log.id));

      if (unsentBatch.length > 0) {
        const success = this.sendLogsWithBeacon(unsentBatch);

        if (success) {
          // Mark logs as sent
          unsentBatch.forEach(log => {
            this.sentLogIds.add(log.id);
          });
        } else {
          // Beacon failed, save to storage
          this.addToRetryQueue(unsentBatch);
        }
      }

      this.batch = [];
    }

    // Save any remaining retry queue to storage
    if (this.retryQueue.length > 0) {
      this.saveFailedLogsToStorage();
    }
  }

  // Start flush timer
  private startFlushTimer(): void {
    if (this.flushTimer) return;

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  // Start periodic flush (backup timer)
  private startPeriodicFlush(): void {
    // Flush every flush interval regardless of batch size
    this.periodicTimer = setInterval(() => {
      if (this.batch.length > 0 && !this.isFlushing) {
        this.flush();
      }
    }, this.config.flushInterval) as unknown as number;
  }

  // Generate unique log ID
  private generateLogId(log: Partial<LogEvent>): string {
    const timestamp = log.timestamp || Date.now();
    const content = JSON.stringify({
      action: log.action,
      category: log.category,
      eventType: log.eventType,
      walletAddress: log.walletAddress,
      timestamp
    });

    // Simple hash function for ID generation
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return `log_${Math.abs(hash)}_${timestamp}`;
  }

  // Clean up old sent log IDs to prevent memory leak
  private cleanupSentLogIds(): void {
    if (this.sentLogIds.size > this.MAX_STORAGE_SIZE) {
      // Keep only the most recent half
      const idsArray = Array.from(this.sentLogIds);
      const keepCount = Math.floor(this.MAX_STORAGE_SIZE / 2);

      this.sentLogIds.clear();
      idsArray.slice(-keepCount).forEach(id => {
        this.sentLogIds.add(id);
      });
    }
  }

  // Cleanup resources
  public destroy(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.periodicTimer) {
      clearInterval(this.periodicTimer);
      this.periodicTimer = null;
    }

    // Final flush attempt
    if (this.batch.length > 0) {
      this.handlePageUnload();
    }

    // Clear sent log IDs
    this.sentLogIds.clear();

    // Remove event listeners
    window.removeEventListener('online', () => {});
    window.removeEventListener('offline', () => {});
    window.removeEventListener('beforeunload', () => {});
    document.removeEventListener('visibilitychange', () => {});
    window.removeEventListener('freeze', () => {});
  }
}
